import { Box, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Layout,
  useMiscellaneousRoutes,
  useUserStore,
} from '@roc/feature-app-core';
import { isNil } from '@roc/feature-utils';
import { StepObj } from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { useHistory, useParams } from 'react-router';
import { useThirdPartyLoanRoutes } from '../hooks/useThirdPartyLoanRoutes';
import { useThirdPartyLoanStore } from '../hooks/useThirdPartyLoanStore';
import { routeByLoanSubType } from '../routes/thirdPartyLoanRoutes';
import {
  QuoteStep,
  SelectEntityOption,
  rentalPortfolioQuoteSteps,
} from '../utils/constants';
import { QuoteTypeStoreContext } from '../utils/quoteTypeStoreContext';
import { CapitalProviderInfo } from './common/capitalProviderInfo';
import { LoanPricerStep } from './common/loanPricerStep';
import { OneToolQuoteStepsContainer } from './common/oneToolQuoteStepsContainer';
import { BorrowerInformationStep } from './fixFlip/borrowerInformationStep';
import { EntityInformationStep } from './fixFlip/entityInformationStep';
import { PortfolioFinalDetailsStep } from './rentalPortfolio/portfolioFinalDetailsStep';
import { LoanSuccessStep } from './fixFlip/loanSuccess';
import { RentalPortfolioLoanDetailsCard } from './rentalPortfolio/rentalPortfolioLoanDetailsCard';
import { PortfolioInformationStep } from './rentalPortfolio/portfolioInformationStep';
import { PortfolioSummaryStep } from './rentalPortfolio/portfolioSummaryStep';

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(5, 7.5),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 0.5),
    },
  },
}));

interface Step extends StepObj {
  info?: string;
  size?: number;
}

export const RentalPortfolioLoanSubmission = observer(() => {
  const { thirdPartyLoansStore } = useThirdPartyLoanStore();
  const { rentalPortfolioStore } = thirdPartyLoansStore;
  const classes = useStyles();
  const { quoteNumber, loanId } = useParams<{ quoteNumber: string; loanId: string }>();
  const { push } = useHistory();
  const { thirdPartyLoanRoutesConfig } = useThirdPartyLoanRoutes();

  const store = rentalPortfolioStore;
  const quoteSteps = rentalPortfolioQuoteSteps;

  const lastCompletedStepIndex = quoteSteps.indexOf(store.lastCompletedStep);
  const { entityStore } = store;
  const { selectEntityOption } = entityStore;
  const savedQuoteNumber = store?.savedQuote?.quoteNumber;
  const isLoan = loanId;

  const title = isLoan
    ? `Edit Loan [${store.savedQuote?.loanNumber}] - Rental Portfolio`
    : 'Submit a Loan - Rental Portfolio';

  const { miscellaneousRoutesConfig } = useMiscellaneousRoutes();

  const disableNavigation = false;

  const init = async () => {
    store.reset();
    if (quoteNumber) {
      await store.loadQuote(quoteNumber);
    } else if (loanId) {
      await store.loadLoan(loanId);
    } else {
      const capitalProviderName = thirdPartyLoansStore.capitalProviderName;
      if (capitalProviderName === undefined) {
        push(thirdPartyLoanRoutesConfig.submitloan.url);
      } else {
        store.setCapitalProviderName(capitalProviderName);
      }
    }
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (store.quoteNotFound) {
      push(miscellaneousRoutesConfig.notFound.url);
    }
  }, [store.quoteNotFound]);

  useEffect(() => {
    if (!isLoan && savedQuoteNumber) {
      const route =
        thirdPartyLoanRoutesConfig[routeByLoanSubType[store.loanSubtype]];
      push(route(savedQuoteNumber).url);
    }
  }, [savedQuoteNumber]);

  if (isNil(store.step)) {
    return null;
  }

  const quoteData = store.getQuoteData();

  const borrowerInfo =
    lastCompletedStepIndex >= quoteSteps.indexOf(QuoteStep.BORROWER_INFORMATION)
      ? quoteData.borrowers
        .map(b => [b.firstName, b.lastName].join(' '))
        .join(', ')
      : '';

  const leverageInfo =
    lastCompletedStepIndex >= quoteSteps.indexOf(QuoteStep.LEVERAGE)
      ? store.loanEconomicsStore.calculateKeyRatios()
      : '';

  const nextButtonText = 'SAVE & CONTINUE';

  const steps: Step[] = [
    {
      title: 'Entity Information',
      icon: null,
      onBack: () => store.entityStore.setSelectEntityOption(null),
      onNext: () => store.goToStep(QuoteStep.BORROWER_INFORMATION),
      allowBack:
        !!selectEntityOption &&
        selectEntityOption !== SelectEntityOption.NO_ENTITY,
      allowNext: !!selectEntityOption,
      nextButtonText: nextButtonText,
      info: borrowerInfo,
      size: 271,
    },
    {
      title: 'Borrower Information',
      icon: null,
      onBack: () => store.goToStep(QuoteStep.ENTITY_INFORMATION),
      onNext: () => store.goToStep(QuoteStep.PORTFOLIO_INFORMATION),
      allowBack: true,
      allowNext: true,
      nextButtonText: nextButtonText,
      info: borrowerInfo,
      size: 271,
    },
    {
      title: 'Portfolio Information',
      icon: null,
      onBack: () => store.goToStep(QuoteStep.BORROWER_INFORMATION),
      onNext: () => {
        const rentalPortfolioQuoteStore = store;
        rentalPortfolioQuoteStore.prepopulateProperties();
        store.goToStep(QuoteStep.LOAN_PRICER);
      },
      allowBack: true,
      allowNext: true,
      nextButtonText: nextButtonText,
      size: 400,
    },
    {
      title: 'Loan Pricer',
      icon: null,
      onBack: () => store.goToStep(QuoteStep.PORTFOLIO_INFORMATION),
      onNext: () => store.goToStep(QuoteStep.SUMMARY),
      allowBack: true,
      allowNext: true,
      nextButtonText: nextButtonText,
      info: leverageInfo,
      size: 130,
    },
    {
      title: 'Summary',
      icon: null,

      onBack: () => store.goToStep(QuoteStep.LOAN_PRICER),
      onNext: () => store.goToStep(QuoteStep.FINAL_DETAILS),
      allowBack: true,
      allowNext: true,
      nextButtonText: nextButtonText,
      size: 115,
    },
  ];

  if (store.step === QuoteStep.FINAL_DETAILS) {
    return (
      <QuoteTypeStoreContext.Provider value={store}>
        <PortfolioFinalDetailsStep store={store} />
      </QuoteTypeStoreContext.Provider>
    );
  }
  if (store.step === QuoteStep.SUCCESS) {
    return (
      <QuoteTypeStoreContext.Provider value={store}>
        <LoanSuccessStep store={store as any} />
      </QuoteTypeStoreContext.Provider>
    );
  }

  if (quoteSteps.indexOf(store.step) === -1) {
    store.step = QuoteStep.PORTFOLIO_INFORMATION;
  }

  return (
    <QuoteTypeStoreContext.Provider value={store}>
      <Layout maxWidth={'lg'}>
        <Box display={'flex'} alignItems={'center'} pb={2}>
          <Typography
            align="left"
            variant="h2"
            style={{
              color: '#5E5E5E',
              fontSize: '48px',
            }}
          >
            {title}
          </Typography>
          <Box flex={1} />
          <CapitalProviderInfo capitalProvider={store.capitalProviderName} />
        </Box>
        <Paper className={classes.paper} elevation={0}>
          <OneToolQuoteStepsContainer
            activeStep={quoteSteps.indexOf(store.step)}
            disableNavigation={disableNavigation}
            steps={steps}
            handleStep={index => store.goToStep(quoteSteps[index])}
          >
            <EntityInformationStep />
            <BorrowerInformationStep store={store as any} />
            <PortfolioInformationStep store={store} />
            <LoanPricerStep store={store as any} />
            <PortfolioSummaryStep
              store={store as any}
              showPointsAndFees={false}
              LoanDetailsCard={RentalPortfolioLoanDetailsCard as any}
            />
          </OneToolQuoteStepsContainer>
        </Paper>
      </Layout>
    </QuoteTypeStoreContext.Provider>
  );
});
